import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { UserResponse } from '@shared/schemas';

import type { UserDraft } from '../../_shared/domain/user-draft.model';
import type { EditUserError } from '../domain/edit-user-error';
import { initialState } from './edit-user.state';

/**
 * Edit-user slice. Reducers are pure state transitions only; the I/O (load /
 * update) lives in the saga, which dispatches these lifecycle transitions as it
 * progresses.
 */
const slice = createSlice({
  name: 'editUser',
  initialState,
  reducers: {
    // ── Load lifecycle (dispatched by the saga) ──
    loadStarted: (state) => {
      state.loadStatus = 'loading';
      state.error = null;
    },
    loadSucceeded: (state, action: PayloadAction<UserResponse>) => {
      const user = action.payload;
      state.user = user;
      state.draft = {
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email,
        linkedinUrl: user.linkedin_url ?? '',
      };
      state.licenceName = user.licence_name ?? user.licence_id;
      state.loadStatus = 'loaded';
    },
    loadFailed: (state, action: PayloadAction<EditUserError>) => {
      state.loadStatus = 'failed';
      state.error = action.payload;
    },
    /** A single field changed; clears that field's validation error. */
    fieldChanged: (
      state,
      action: PayloadAction<{ field: keyof UserDraft; value: string }>,
    ) => {
      const { field, value } = action.payload;
      state.draft[field] = value;
      delete state.validationErrors[field];
    },
    // ── Save lifecycle ──
    saveStarted: (state) => {
      state.saveStatus = 'saving';
      state.error = null;
      state.validationErrors = {};
    },
    saveSucceeded: (state) => {
      state.saveStatus = 'succeeded';
    },
    saveFailed: (state, action: PayloadAction<EditUserError>) => {
      state.saveStatus = 'failed';
      state.error = action.payload;
      if (action.payload.kind === 'validation') {
        state.validationErrors = action.payload.fields;
      }
    },
    /** Reset everything to the initial state (on unmount / navigation away). */
    resetForm: () => initialState,
  },
});

export const editUserActions = slice.actions;
export const editUserReducer = slice.reducer;
